import React from "react";
import { Card } from "@/components/ui/card";
import { Text } from "@/components/ui/text";
import PunchCardsAsciiLogo from "./PunchCardsAsciiLogo";
import { getAvatarProfile } from "../data/avatarData";

// Default scoring values from application defaults
const DEFAULT_POINTS_PER_REPLY = 2;
const DEFAULT_POINTS_PER_CLOSED_TICKET = 10;
const DEFAULT_POINTS_PER_POSITIVE_CSAT = 60; // Using max band for demo

interface PunchCardPreviewProps {
  agent?: number; // Avatar ID for the first employee (1-12)
  manager?: number; // Avatar ID for the manager (1-12)
  repliesCount?: number;
  closedTicketsCount?: number;
  positiveCSATsCount?: number;
  message?: string;
}

const PunchCardPreview: React.FunctionComponent<PunchCardPreviewProps> = ({
  agent = 1,
  manager = 2,
  repliesCount = 12,
  closedTicketsCount = 1,
  positiveCSATsCount = 1,
  message = "Great work yesterday! Seize the day today.",
}) => {
  // Get avatar profiles
  const agentProfile = getAvatarProfile(agent);
  const managerProfile = getAvatarProfile(manager);
  
  // Fallback names if profiles not found
  const firstName = agentProfile?.name || "Agent";
  //eslint-disable-next-line @typescript-eslint/no-unused-vars -- We want this for later
  const secondName = managerProfile?.name || "Manager";
  

  
  // Calculate points based on counts and default scoring
  const repliesPoints = repliesCount * DEFAULT_POINTS_PER_REPLY;
  const closedTicketsPoints = closedTicketsCount * DEFAULT_POINTS_PER_CLOSED_TICKET;
  const positiveCSATsPoints = positiveCSATsCount * DEFAULT_POINTS_PER_POSITIVE_CSAT;
  const totalPoints = repliesPoints + closedTicketsPoints + positiveCSATsPoints;

  return (
    <Card className="bg-card/95 backdrop-blur-sm shadow-lg p-6 max-w-2xl">
      {/* Slack Message Header */}
      <div className="flex items-center mb-2">
        {/* Agent Profile Photo */}
        <div className="relative size-10 rounded-full overflow-hidden border border-card shadow-sm">
          {agentProfile ? (
            <img 
              src={agentProfile.avatarPath} 
              alt={firstName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-highlight-500 flex items-center justify-center text-neutral-0 font-semibold text-lg">
              {firstName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        
        {/* Name */}
        <div className="flex-1 ml-3">
          <Text as="div" variant="sm" className="font-semibold text-foreground">
            {firstName}
          </Text>
        </div>
      </div>

      {/* Scorecard */}
      <div className="bg-background rounded p-4 border border-border">
        {/* App Name Header */}
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-border">
          <Text as="span" variant="sm" className="font-semibold text-card-foreground font-mono">
            <PunchCardsAsciiLogo /> punch.cards
          </Text>
        </div>

        {/* Score Lines */}
        <div className="space-y-2 mb-3">
          <Text as="div" variant="sm" className="flex items-center gap-2 font-mono">
            <Text as="span" variant="sm" className="font-bold text-card-foreground min-w-[3rem] font-mono">
              +{repliesPoints}
            </Text>
            <Text as="span" variant="sm" className="text-card-foreground font-mono">
              for {repliesCount} replies
            </Text>
          </Text>
          
          <Text as="div" variant="sm" className="flex items-center gap-2 font-mono">
            <Text as="span" variant="sm" className="font-bold text-card-foreground min-w-[3rem] font-mono">
              +{closedTicketsPoints}
            </Text>
            <Text as="span" variant="sm" className="text-card-foreground font-mono">
              for {closedTicketsCount} closed tickets
            </Text>
          </Text>
          
          <Text as="div" variant="sm" className="flex items-center gap-2 font-mono">
            <Text as="span" variant="sm" className="font-bold text-card-foreground min-w-[3rem] font-mono">
              +{positiveCSATsPoints}
            </Text>
            <Text as="span" variant="sm" className="text-card-foreground font-mono">
              for {positiveCSATsCount} positive CSATs
            </Text>
          </Text>
        </div>

        {/* Total Points and Message */}
        <div className="pt-3 border-t border-border">
          <Text variant="sm" className="font-mono mb-2">
            <Text as="span" variant="sm" className="font-bold text-card-foreground font-mono">+{totalPoints} points</Text>
          </Text>
          <Text variant="sm" className="text-card-foreground italic font-mono">
            {message}
          </Text>
        </div>
      </div>
    </Card>
  );
};

export default PunchCardPreview;
